import { useEffect, useState } from 'react';
import { financeiroService, FinanceiroResumo, auditoriaService, AuditoriaRegistro } from '../../services';

type PeriodoFin = 'hoje' | 'ontem' | 'semana' | 'mes' | 'custom';

/** T140: rótulos do log de auditoria */
const REGISTRO_LABELS: Record<string, string> = {
  FECHOU_CONTA: 'Fechou conta',
  CANCELOU_COMANDA: 'Cancelou comanda',
  CANCELOU_ITEM: 'Cancelou item',
};

const PERIODOS: Array<{ valor: PeriodoFin; label: string }> = [
  { valor: 'hoje', label: 'Hoje' },
  { valor: 'ontem', label: 'Ontem' },
  { valor: 'semana', label: 'Últimos 7 dias' },
  { valor: 'mes', label: 'Últimos 30 dias' },
  { valor: 'custom', label: 'Personalizado' },
];

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dia}`;
}

function datasDoPeriodo(p: PeriodoFin): { inicio: string; fim: string } {
  const hoje = new Date();
  if (p === 'hoje') return { inicio: ymd(hoje), fim: ymd(hoje) };
  if (p === 'ontem') {
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    return { inicio: ymd(ontem), fim: ymd(ontem) };
  }
  if (p === 'semana') {
    const ini = new Date(hoje);
    ini.setDate(hoje.getDate() - 6);
    return { inicio: ymd(ini), fim: ymd(hoje) };
  }
  if (p === 'mes') {
    const ini = new Date(hoje);
    ini.setDate(hoje.getDate() - 29);
    return { inicio: ymd(ini), fim: ymd(hoje) };
  }
  return { inicio: ymd(hoje), fim: ymd(hoje) };
}

function fmtBr(v: number): string {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDia(dia: string): string {
  const [y, m, d] = dia.split('-');
  return `${d}/${m}/${y}`;
}

export default function Financeiro() {
  const [periodo, setPeriodo] = useState<PeriodoFin>('hoje');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [stats, setStats] = useState<FinanceiroResumo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [exportando, setExportando] = useState(false);
  // T140: log de auditoria das ações que mexem em dinheiro
  const [auditoria, setAuditoria] = useState<AuditoriaRegistro[]>([]);

  useEffect(() => {
    auditoriaService
      .listar(30)
      .then((r) => setAuditoria(r.data.data))
      .catch(() => setAuditoria([]));
  }, []);

  const { inicio, fim } =
    periodo === 'custom' && dataInicio && dataFim
      ? { inicio: dataInicio, fim: dataFim }
      : datasDoPeriodo(periodo);

  useEffect(() => {
    let ativo = true;
    setIsLoading(true);
    setErro('');
    financeiroService
      .resumo(inicio, fim)
      .then((r) => {
        if (ativo) setStats(r.data.data);
      })
      .catch(() => {
        if (ativo) {
          setErro('Não foi possível carregar o relatório financeiro');
          setStats(null);
        }
      })
      .finally(() => {
        if (ativo) setIsLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [inicio, fim]);

  const exportarCsv = async () => {
    setExportando(true);
    try {
      const r = await financeiroService.exportar(inicio, fim);
      const comandas = r.data.data;
      const linhas = [
        ['Data', 'Mesa', 'Cliente', 'Itens', 'Total (R$)'],
        ...comandas.map((c) => [c.data, c.mesa, c.cliente, c.itens, c.total]),
      ];
      const csv = linhas
        .map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
        .join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `financeiro-${inicio}-a-${fim}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert('Erro ao exportar o relatório');
    } finally {
      setExportando(false);
    }
  };

  const s = stats?.resumo;
  const maxDia = stats ? Math.max(1, ...stats.porDia.map((d) => d.receita)) : 1;
  const totalReceita = s?.receita ?? 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">💰 Financeiro</h1>
          <p className="text-sm text-gray-400">Relatório de receita, vendas e comandas do período</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value as PeriodoFin)}
            className="input-field w-auto"
          >
            {PERIODOS.map((p) => (
              <option key={p.valor} value={p.valor}>
                {p.label}
              </option>
            ))}
          </select>
          {periodo === 'custom' && (
            <>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="input-field w-auto"
              />
              <span className="text-gray-500">até</span>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="input-field w-auto"
              />
            </>
          )}
          <button onClick={exportarCsv} className="btn-secondary text-sm" disabled={exportando}>
            {exportando ? 'Gerando...' : '⬇️ Exportar CSV'}
          </button>
        </div>
      </div>

      {erro && (
        <div className="bg-red-950/60 border border-red-800 text-red-300 px-4 py-3 rounded-lg">{erro}</div>
      )}

      {isLoading && !stats ? (
        <p className="text-gray-400">Carregando relatório...</p>
      ) : (
        s && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <div className="card p-4">
                <p className="text-xs uppercase tracking-wider text-gray-500">Faturamento</p>
                <p className="text-2xl font-extrabold text-primary-400 mt-1">R$ {fmtBr(s.receita)}</p>
                <p className="text-xs text-gray-500 mt-1">{s.comandasFechadas} comandas fechadas</p>
              </div>
              <div className="card p-4">
                <p className="text-xs uppercase tracking-wider text-gray-500">Ticket médio</p>
                <p className="text-2xl font-extrabold text-gray-100 mt-1">R$ {fmtBr(s.ticketMedio)}</p>
                <p className="text-xs text-gray-500 mt-1">por comanda fechada</p>
              </div>
              <div className="card p-4">
                <p className="text-xs uppercase tracking-wider text-gray-500">Itens vendidos</p>
                <p className="text-2xl font-extrabold text-gray-100 mt-1">{s.itensVendidos}</p>
                <p className="text-xs text-gray-500 mt-1">{s.pedidosTotal} pedidos no período</p>
              </div>
              <div className="card p-4">
                <p className="text-xs uppercase tracking-wider text-gray-500">Em aberto</p>
                <p className="text-2xl font-extrabold text-amber-400 mt-1">R$ {fmtBr(s.emAberto)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {s.emAbertoQtd} {s.emAbertoQtd === 1 ? 'comanda aberta' : 'comandas abertas'}
                </p>
              </div>
              <div className="card p-4">
                <p className="text-xs uppercase tracking-wider text-gray-500">Cancelados</p>
                <p className="text-2xl font-extrabold text-red-400 mt-1">R$ {fmtBr(s.cancelados)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {s.canceladosQtd} {s.canceladosQtd === 1 ? 'comanda cancelada' : 'comandas canceladas'}
                </p>
              </div>
              <div className="card p-4">
                <p className="text-xs uppercase tracking-wider text-gray-500">Concluído</p>
                <p className="text-2xl font-extrabold text-green-400 mt-1">{s.comandasFechadas}</p>
                <p className="text-xs text-gray-500 mt-1">comandas pagas</p>
              </div>
            </div>

            {stats.porDia.length > 0 && (
              <div className="card p-4">
                <h2 className="text-lg font-bold text-gray-100 mb-4">Faturamento por dia</h2>
                <div className="space-y-2">
                  {stats.porDia.map((d) => (
                    <div key={d.dia} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 text-sm text-gray-400">{fmtDia(d.dia)}</span>
                      <div className="flex-1 h-5 bg-gray-800 rounded overflow-hidden">
                        <div
                          className="h-full bg-primary-500/80 rounded"
                          style={{ width: `${Math.max(2, (d.receita / maxDia) * 100)}%` }}
                        />
                      </div>
                      <span className="w-24 shrink-0 text-right text-sm font-semibold text-gray-100">
                        R$ {fmtBr(d.receita)}
                      </span>
                      <span className="w-14 shrink-0 text-right text-xs text-gray-500">{d.comandas} com.</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div className="card p-4">
                <h2 className="text-lg font-bold text-gray-100 mb-3">Receita por categoria</h2>
                {stats.porCategoria.length === 0 ? (
                  <p className="text-gray-500 text-sm">Nenhuma venda no período.</p>
                ) : (
                  <div className="space-y-2">
                    {stats.porCategoria.map((c) => {
                      const pct = totalReceita > 0 ? (c.receita / totalReceita) * 100 : 0;
                      return (
                        <div key={c.nome} className="flex items-center justify-between gap-2 text-sm">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-100 truncate">{c.nome}</p>
                            <p className="text-xs text-gray-500">
                              {c.quantidade} {c.quantidade === 1 ? 'item' : 'itens'} · {pct.toFixed(1)}%
                            </p>
                          </div>
                          <span className="font-semibold text-gray-100 whitespace-nowrap">
                            R$ {fmtBr(c.receita)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="card p-4">
                <h2 className="text-lg font-bold text-gray-100 mb-3">Receita por produto</h2>
                {stats.porProduto.length === 0 ? (
                  <p className="text-gray-500 text-sm">Nenhuma venda no período.</p>
                ) : (
                  <div className="space-y-2">
                    {stats.porProduto.map((p, i) => {
                      const pct = totalReceita > 0 ? (p.receita / totalReceita) * 100 : 0;
                      return (
                        <div key={p.nome} className="flex items-center justify-between gap-2 text-sm">
                          <div className="min-w-0 flex items-center gap-2">
                            <span className="w-5 shrink-0 text-xs text-gray-500">{i + 1}º</span>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-100 truncate">{p.nome}</p>
                              <p className="text-xs text-gray-500">
                                {p.quantidade} {p.quantidade === 1 ? 'vendido' : 'vendidos'} · {pct.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                          <span className="font-semibold text-gray-100 whitespace-nowrap">
                            R$ {fmtBr(p.receita)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {stats.porMesa.length > 0 && (
              <div className="card p-4">
                <h2 className="text-lg font-bold text-gray-100 mb-3">Receita por mesa</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-800">
                        <th className="py-2 pr-4 font-medium">Mesa</th>
                        <th className="py-2 pr-4 font-medium">Comandas fechadas</th>
                        <th className="py-2 font-medium text-right">Receita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.porMesa.map((m) => (
                        <tr key={m.mesa} className="border-b border-gray-800/60">
                          <td className="py-2 pr-4 font-bold text-primary-400">Mesa {m.mesa}</td>
                          <td className="py-2 pr-4 text-gray-300">{m.comandas}</td>
                          <td className="py-2 text-right font-semibold text-gray-100">R$ {fmtBr(m.receita)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {stats.porDia.length === 0 && (
              <div className="card p-4 text-center text-gray-500">
                Nenhuma comanda fechada no período selecionado.
              </div>
            )}
          </>
        )
      )}

      {/* T140: auditoria — quem cancelou/fechou o quê, com motivo e horário */}
      <div className="card p-4 mt-6">
        <h2 className="text-lg font-bold text-gray-100 mb-1">🔎 Auditoria das ações de caixa</h2>
        <p className="text-xs text-gray-500 mb-3">
          Cancelamentos e fechamentos de conta, com autor e motivo. Últimos 30 registros.
        </p>
        {auditoria.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhuma ação registrada ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-800">
                  <th className="py-2 pr-4 font-medium">Quando</th>
                  <th className="py-2 pr-4 font-medium">Ação</th>
                  <th className="py-2 pr-4 font-medium">Detalhe</th>
                  <th className="py-2 font-medium">Autor</th>
                </tr>
              </thead>
              <tbody>
                {auditoria.map((a) => (
                  <tr key={a.id} className="border-b border-gray-800/60">
                    <td className="py-2 pr-4 text-gray-400 whitespace-nowrap">
                      {new Date(a.criado_em).toLocaleString('pt-BR')}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          a.acao === 'FECHOU_CONTA'
                            ? 'bg-green-900/40 text-green-300'
                            : 'bg-red-900/40 text-red-300'
                        }`}
                      >
                        {REGISTRO_LABELS[a.acao] ?? a.acao}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-gray-300">{a.detalhe ?? '—'}</td>
                    <td className="py-2 text-gray-400">{a.autor_nome ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}